import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
} from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlank from '@material-ui/icons/CheckBoxOutlineBlank';
import { Autocomplete } from '@material-ui/lab';
import type { CSSProperties } from 'react';
import { ChangeEvent, Fragment } from 'react';
import { useIamPolicies } from '../hooks/useIamPolicies';

export interface PhxIamPolicyMultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  helperText?: string;
  error?: boolean;
  fullWidth?: boolean;
  variant?: 'standard' | 'outlined' | 'filled';
  size?: 'small' | 'medium';
  style?: CSSProperties;
}

type Option = { label: string; value: string };
type AutocompleteValue = Option | string;

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
function tryParseArrayFromString(value: string): string[] | null {
  const trimmed = value.trim();
  if (!trimmed.startsWith('[') || !trimmed.endsWith(']')) {
    return null;
  }
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed.map(v => String(v));
    }
  } catch {
    return null;
  }
  return null;
}

export const PhxIamPolicyMultiSelect = ({
  value,
  onChange,
  label = 'IAM Policies',
  helperText,
  error = false,
  fullWidth = true,
  variant = 'standard',
  size = 'medium',
  style,
}: PhxIamPolicyMultiSelectProps) => {
  const { options, loading, error: fetchError } = useIamPolicies();
  let normalizedValue: string[] = [];
  if (Array.isArray(value)) {
    normalizedValue = value;
  } else if (value) {
    normalizedValue = tryParseArrayFromString(String(value)) ?? [String(value)];
  }

  const handleChange = (_event: ChangeEvent<{}>, newValue: AutocompleteValue[]) => {
    onChange(newValue.map(v => (typeof v === 'string' ? v : v.value)).filter(Boolean));
  };

  // Convert value array to option objects for Autocomplete
  const selectedOptions = normalizedValue.map(val => {
    return options.find(opt => opt.value === val) ?? { label: val, value: val };
  });

  const mergedOptions = [
    ...options,
    ...selectedOptions.filter(sel => !options.some(opt => opt.value === sel.value)),
  ];

  if (loading) {
    return (
      <FormControl fullWidth={fullWidth} style={style}>
        <Box display="flex" alignItems="center" p={2}>
          <CircularProgress size={20} style={{ marginRight: 8 }} />
          <Typography variant="body2" color="textSecondary">
            Loading IAM policies…
          </Typography>
        </Box>
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth={fullWidth} error={error} style={style}>
      <Autocomplete
        multiple
        options={mergedOptions}
        value={selectedOptions}
        onChange={handleChange}
        disableCloseOnSelect
        size={size}
        getOptionLabel={option => (typeof option === 'string' ? option : option.label)}
        renderOption={(option, { selected }) => (
          <Fragment>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {typeof option === 'string' ? option : option.label}
          </Fragment>
        )}
        renderInput={params => (
          <TextField
            {...params}
            variant={variant}
            label={label}
            error={error}
            size={size}
            placeholder={helperText}
          />
        )}
      />
      {fetchError ? (
        <FormHelperText>
          Failed to load IAM policies from the backend. (
          {fetchError instanceof Error ? fetchError.message : String(fetchError)})
        </FormHelperText>
      ) : (
        helperText && <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};
